import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  LogOut,
  Home,
  BarChart3,
  Users,
  Tag,
  BookOpen,
  FolderOpen,
  FileText,
  Settings,
  AppWindow,
  Wallet,
  CreditCard,
  ChevronRight,
  SlidersHorizontal,
  Calculator,
  Smartphone,
  Layers,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import * as Collapsible from "@radix-ui/react-collapsible";

import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
} from "@/shared/ui/sidebar";
import { Button } from "@/shared/ui/button";
import { useAuth } from "@/features/auth/model/AuthContext";
import { Permission, UserRole, isCreator } from "@/shared/types/roles";
import { useToast } from "@/shared/hooks/use-toast";
import { useNavigate } from "react-router-dom";

import { WithPermission } from "../auth/WithPermission";
import { useTranslation } from '@/shared/i18n';

type NavigationPermissions = {
  permission?: Permission;
  permissions?: Permission[];
  adminOnly?: boolean;
  /** Se true, item só aparece para creators (PHOTOGRAPHER / INFLUENCER) */
  creatorOnly?: boolean;
};

type NavigationLinkItem = NavigationPermissions & {
  titleKey: string;
  url: string;
  icon: LucideIcon;
};

type NavigationParentItem = NavigationPermissions & {
  titleKey: string;
  icon: LucideIcon;
  children: NavigationLinkItem[];
};

type NavigationItem = NavigationLinkItem | NavigationParentItem;

const getNavigationItems = (t: (key: string) => string, userRole: UserRole | null): NavigationItem[] => {
  const base: NavigationItem[] = [
  {
    titleKey: "home",
    url: "/",
    icon: Home,
    permission: Permission.PACKS_VIEW,
  },
  {
    titleKey: "statistics",
    url: "/stats",
    icon: BarChart3,
    permission: Permission.ANALYTICS_VIEW,
  },
  {
    titleKey: "appAnalytics",
    url: "/app-analytics",
    icon: AppWindow,
    adminOnly: true, // Apenas para admins durante o beta
  },
  {
    titleKey: "users",
    url: "/users",
    icon: Users,
    permission: Permission.USERS_VIEW,
  },
  {
    titleKey: "contracts",
    url: "/contracts",
    icon: FileText,
    permissions: [Permission.CONTRACTS_VIEW, Permission.CONTRACTS_VIEW],
  },
  {
    titleKey: "tags",
    url: "/tags",
    icon: Tag,
    permission: Permission.TAGS_VIEW,
  },
  {
    titleKey: "learnings",
    url: "/learning",
    icon: BookOpen,
    permission: Permission.LEARNING_VIEW,
  },
  {
    titleKey: "categories",
    url: "/categories",
    icon: FolderOpen,
    permission: Permission.CATEGORIES_VIEW,
  },
  {
    titleKey: "mobile",
    icon: Smartphone,
    children: [
      {
        titleKey: "manageSections",
        url: "/admin/highlights",
        icon: Settings,
        permission: Permission.HIGHLIGHTS_VIEW,
      },
      {
        titleKey: "collections",
        url: "/mobile/collections",
        icon: Layers,
        permission: Permission.HIGHLIGHTS_VIEW,
      },
    ],
  },
  {
    titleKey: "financial",
    icon: Wallet,
    permission: Permission.RECEIVABLES_VIEW, // Admin: só quem tem permissão financeira vê esta aba
    children: [
      {
        titleKey: "receivables",
        url: "/receivables",
        icon: BarChart3,
        permission: Permission.RECEIVABLES_VIEW,
      },
      {
        titleKey: "payments",
        url: "/payments",
        icon: CreditCard,
        permission: Permission.PAYMENTS_VIEW,
      },
      {
        titleKey: "calculation",
        url: "/receivable-snapshots",
        icon: Calculator,
        permission: Permission.RECEIVABLES_VIEW,
      },
      {
        titleKey: "shareConfig",
        url: "/receivable-share-configs",
        icon: SlidersHorizontal,
        permission: Permission.RECEIVABLES_VIEW,
      },
    ],
  },
  ];

  // Aba "Meu Financeiro" para creators; admin também vê para acessar as telas do creator
  if (userRole && (isCreator(userRole) || userRole === UserRole.ADMIN)) {
    base.push({
      titleKey: "creatorFinancial",
      icon: Wallet,
      creatorOnly: true,
      children: [
        {
          titleKey: "creatorBankAccount",
          url: "/creator/bank-accounts",
          icon: CreditCard,
        },
        {
          titleKey: "creatorPayments",
          url: "/creator/receivables",
          icon: Wallet,
        },
      ],
    });
  }

  return base;
};

const getItemPermissions = (
  item: NavigationPermissions,
): Permission[] | undefined => {
  if (item.permissions && item.permissions.length > 0) {
    return item.permissions;
  }
  if (item.permission) {
    return [item.permission];
  }
  return undefined;
};

const canAccessItem = (
  item: NavigationPermissions,
  userRole: UserRole | null,
  hasAnyPermission: (permissions: Permission[]) => boolean,
) => {
  if (item.creatorOnly) {
    return (
      userRole != null &&
      (isCreator(userRole) || userRole === UserRole.ADMIN)
    );
  }
  if (item.adminOnly && userRole !== UserRole.ADMIN) {
    return false;
  }

  const permissions = getItemPermissions(item);

  if (!permissions || permissions.length === 0) {
    return true;
  }

  return hasAnyPermission(permissions);
};

const getErrorMessage = (error: unknown, t: (key: string) => string): string => {
  if (error instanceof Error && error.message) {
    return error.message;
  }

  if (
    typeof error === "object" &&
    error !== null &&
    "message" in error &&
    typeof (error as { message?: unknown }).message === "string"
  ) {
    return (error as { message: string }).message;
  }

  if (typeof error === "string" && error) {
    return error;
  }

  return t('logoutErrorDefault');
};

export function AppSidebar() {
  const { t } = useTranslation('navigation');
  const location = useLocation();
  const { logout, user, hasAnyPermission } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>({});
  const userRole = user?.role ?? null;
  const navigationItems = getNavigationItems(t, userRole);

  const handleLogout = async () => {
    try {
      logout();
      navigate("/auth");
    } catch (error: unknown) {
      const errorMessage = getErrorMessage(error, t);
      toast({
        variant: "destructive",
        title: t('logoutError'),
        description: errorMessage,
      });
    }
  };

  const isCurrentPath = (path: string) => {
    if (path === "/") {
      return location.pathname === "/";
    }
    return location.pathname.startsWith(path);
  };

  return (
    <Sidebar
      variant="inset"
      collapsible="icon"
      className="bg-[#1A1A1A] border-r border-white/10"
    >
      <SidebarHeader className="border-b border-white/10 bg-[#1A1A1A]">
        <div className="flex items-center gap-2 px-2 py-2">
          {/* Logo expandido */}
          <div className="group-data-[collapsible=icon]:hidden">
            <img
              src="/lovable-uploads/logo_likeappro.svg"
              alt="LikeAPPro Logo"
              className="h-8 w-auto"
            />
          </div>
          {/* Ícone colapsado */}
          <div className="hidden group-data-[collapsible=icon]:block">
            <img
              src="/lovable-uploads/icon_likeappro.svg"
              alt="LikeAPPro Icon"
              className="h-8 w-auto"
            />
          </div>
        </div>
      </SidebarHeader>

      <SidebarContent className="bg-[#1A1A1A]">
        <SidebarGroup>
          <SidebarGroupContent>
            <SidebarMenu>
              {navigationItems.map((item) => {
                if ("children" in item) {
                  if (!canAccessItem(item, userRole, hasAnyPermission)) {
                    return null;
                  }

                  const accessibleChildren = item.children.filter((child) =>
                    canAccessItem(child, userRole, hasAnyPermission),
                  );

                  if (accessibleChildren.length === 0) {
                    return null;
                  }

                  const hasActiveChild = accessibleChildren.some((child) =>
                    isCurrentPath(child.url),
                  );
                  const storedState = openGroups[item.titleKey];
                  const isOpen =
                    hasActiveChild || (storedState !== undefined ? storedState : false);

                  return (
                    <Collapsible.Root
                      key={item.titleKey}
                      open={isOpen}
                      onOpenChange={(nextOpen) =>
                        setOpenGroups((prev) => ({ ...prev, [item.titleKey]: nextOpen }))
                      }
                    >
                      <SidebarMenuItem>
                        <Collapsible.Trigger asChild>
                          <SidebarMenuButton
                            tooltip={t(item.titleKey)}
                            className="text-white/70 hover:text-white hover:bg-white/10 data-[active=true]:bg-[#C1174F] data-[active=true]:text-white group"
                          >
                            <item.icon className="h-4 w-4" />
                            <span>{t(item.titleKey)}</span>
                            <ChevronRight className="ml-auto h-4 w-4 text-white/60 transition-transform group-data-[collapsible=icon]:hidden group-data-[state=open]:rotate-90" />
                          </SidebarMenuButton>
                        </Collapsible.Trigger>
                        <Collapsible.Content>
                          <SidebarMenuSub>
                            {accessibleChildren.map((child) => (
                              <SidebarMenuSubItem key={child.url}>
                                <SidebarMenuSubButton
                                  asChild
                                  isActive={isCurrentPath(child.url)}
                                  className="text-white/70 hover:text-white hover:bg-white/10 data-[active=true]:bg-[#C1174F] data-[active=true]:text-white"
                                >
                                  <Link to={child.url}>
                                    <child.icon className="h-4 w-4" />
                                    <span>{t(child.titleKey)}</span>
                                  </Link>
                                </SidebarMenuSubButton>
                              </SidebarMenuSubItem>
                            ))}
                          </SidebarMenuSub>
                        </Collapsible.Content>
                      </SidebarMenuItem>
                    </Collapsible.Root>
                  );
                }

                if (!canAccessItem(item, userRole, hasAnyPermission)) {
                  return null;
                }

                const permissions = getItemPermissions(item);

                return (
                  <SidebarMenuItem key={item.url}>
                    <WithPermission permissions={permissions}>
                      <SidebarMenuButton
                        asChild
                        isActive={isCurrentPath(item.url)}
                        tooltip={t(item.titleKey)}
                        className="text-white/70 hover:text-white hover:bg-white/10 data-[active=true]:bg-[#C1174F] data-[active=true]:text-white"
                      >
                        <Link to={item.url}>
                          <item.icon className="h-4 w-4" />
                          <span>{t(item.titleKey)}</span>
                        </Link>
                      </SidebarMenuButton>
                    </WithPermission>
                  </SidebarMenuItem>
                );
              })}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>

      <SidebarFooter className="border-t border-white/10 bg-[#1A1A1A]">
        <div className="p-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={handleLogout}
            className="w-full justify-start text-white/70 hover:text-white hover:bg-white/10"
          >
            <LogOut className="h-4 w-4" />
            <span className="group-data-[collapsible=icon]:hidden">{t('logout')}</span>
          </Button>
        </div>
      </SidebarFooter>
    </Sidebar>
  );
}
